import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  StarIcon as StarIconSolid,
  HeartIcon as HeartIconSolid,
  ShoppingCartIcon,
  ShareIcon,
  TruckIcon,
  ShieldCheckIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  MinusIcon,
  TagIcon
} from '@heroicons/react/24/solid';
import {
  StarIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import axios from '../utils/axios';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { RelatedProducts } from '../components/recommendations';
import { useEcommerceTracking } from '../hooks/useAnalytics';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const id = productId;
  const navigate = useNavigate();
  const { addToCart, isLoading: cartLoading } = useCart();
  const { user } = useAuth();
  const { trackProductView, trackAddToCartEvent, trackWishlistAdd } = useEcommerceTracking();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/products/${id}`);
      const productData = response.data.product;
      setProduct(productData);
      
      // Track product view for analytics
      if (productData) {
        trackProductView(productData);
      }
      
      // Add to recently viewed products
      addToRecentlyViewed(productData);
      
    } catch (error) {
      setError('מוצר לא נמצא');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchRelatedProducts = useCallback(async () => {
    try {
      const response = await axios.get(`/api/products/${id}/related`);
      setRelatedProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  }, [id]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`/api/products/${id}/reviews`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchProduct();
      fetchRelatedProducts();
      fetchReviews();
      if (user) {
        checkWishlistStatus();
      }
    }
  }, [id, fetchProduct, fetchRelatedProducts, fetchReviews, user]);

  const checkWishlistStatus = async () => {
    if (!user || !id) return;
    
    try {
      const response = await axios.get(`/api/wishlist/check/${id}`);
      setIsWishlisted(response.data.isWishlisted);
    } catch (error) {
      console.error('Error checking wishlist status:', error);
    }
  };

  const addToRecentlyViewed = (product) => {
    const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    const filtered = recentlyViewed.filter(p => p.id !== product.id);
    const updated = [product, ...filtered].slice(0, 10); // Keep last 10
    localStorage.setItem('recentlyViewed', JSON.stringify(updated));
  };

  const handleAddToCart = async () => {
    if (!product || cartLoading) return;
    
    try {
      await addToCart(product, quantity);
      
      // Track add to cart event for analytics
      trackAddToCartEvent(product, quantity);
      
      // Show success message or animation
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    try {
      if (isWishlisted) {
        await axios.delete(`/api/wishlist/${product.id}`);
        setIsWishlisted(false);
      } else {
        await axios.post(`/api/wishlist`, { product_id: product.id });
        setIsWishlisted(true);
        
        // Track add to wishlist event for analytics
        trackWishlistAdd(product);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.description,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('הקישור הועתק ללוח');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await axios.post(`/api/products/${id}/reviews`, reviewForm);
      
      // Add the new review to the reviews list
      setReviews(prev => [response.data.review, ...prev]);
      
      // Reset form
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
      
      // Show success message
      alert('הביקורת נוספה בהצלחה!');
      
      // Refresh product data to get updated rating
      fetchProduct();
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('שגיאה בשליחת הביקורת');
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(price);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i}>
        {i < Math.floor(rating) ? (
          <StarIconSolid className="h-4 w-4 text-yellow-400" />
        ) : (
          <StarIcon className="h-4 w-4 text-gray-300" />
        )}
      </span>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'מוצר לא נמצא'}</h1>
          <Link to="/products" className="text-primary-600 hover:text-primary-700">
            חזרה לרשימת המוצרים
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images && product.images.length > 0 ? product.images : ['/placeholder-product.jpg'];
  const currentPrice = user?.role === 'company_admin' || user?.role === 'company_user' 
    ? product.price_b2b 
    : product.price_b2c;

  return (
    <div className="min-h-screen bg-white pb-20 lg:pb-0">
      {/* Breadcrumb - Hidden on mobile for space */}
      <div className="hidden sm:block bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700">בית</Link>
            <ChevronLeftIcon className="h-4 w-4 text-gray-400" />
            <Link to="/products" className="text-gray-500 hover:text-gray-700">מוצרים</Link>
            <ChevronLeftIcon className="h-4 w-4 text-gray-400" />
            {product.category && (
              <>
                <Link to={`/products?category=${product.category.id}`} className="text-gray-500 hover:text-gray-700">
                  {product.category.name}
                </Link>
                <ChevronLeftIcon className="h-4 w-4 text-gray-400" />
              </>
            )}
            <span className="text-gray-900 truncate">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Mobile-First Layout: Single column on mobile, two columns on desktop */}
        <div className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-8 lg:space-y-0 mb-12">
          {/* Product Images - Mobile-First with Touch Support */}
          <div className="space-y-3 sm:space-y-4">
            {/* Main Image - Mobile optimized with swipe indicators */}
            <div className="aspect-square bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden relative group touch-pan-x">
              {images[selectedImageIndex] ? (
                <img
                  src={images[selectedImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <PhotoIcon className="h-16 w-16 sm:h-24 sm:w-24 text-gray-400" />
                </div>
              )}
              
              {/* Image Navigation - Always visible on mobile, hover on desktop */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : images.length - 1)}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 sm:p-2.5 shadow-lg lg:opacity-0 lg:group-hover:opacity-100 transition-opacity touch-manipulation"
                    aria-label="Previous image"
                  >
                    <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                  <button
                    onClick={() => setSelectedImageIndex(prev => prev < images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 sm:p-2.5 shadow-lg lg:opacity-0 lg:group-hover:opacity-100 transition-opacity touch-manipulation"
                    aria-label="Next image"
                  >
                    <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                  
                  {/* Image Counter - Mobile only */}
                  <div className="lg:hidden absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                    {selectedImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail Images - Horizontal scroll on mobile */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all touch-manipulation ${
                      selectedImageIndex === index ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info - Mobile-First Spacing */}
          <div className="space-y-4 sm:space-y-6">
            {/* Title and Rating - Mobile optimized text sizes */}
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              {product.name_en && (
                <p className="text-lg text-gray-600 mb-2">{product.name_en}</p>
              )}
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  {renderStars(product.average_rating || 0)}
                  <span className="text-sm text-gray-600 mr-2">
                    ({product.review_count || 0} ביקורות)
                  </span>
                </div>
                {product.sku && (
                  <span className="text-sm text-gray-500">מק"ט: {product.sku}</span>
                )}
              </div>
            </div>

            {/* Price - Mobile-First */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-2xl sm:text-3xl font-bold text-primary-600">
                  {formatPrice(currentPrice)}
                </span>
                {product.price_b2c !== currentPrice && (
                  <span className="text-lg text-gray-500 line-through">
                    {formatPrice(product.price_b2c)}
                  </span>
                )}
              </div>
              
              {user?.role === 'company_admin' || user?.role === 'company_user' ? (
                <p className="text-sm text-blue-600 font-medium">מחיר B2B</p>
              ) : (
                <p className="text-sm text-gray-600">כולל מע"מ</p>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center space-x-2">
              {product.stock_quantity > 0 ? (
                <>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-600 font-medium">במלאי ({product.stock_quantity} יחידות)</span>
                </>
              ) : (
                <>
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-red-600 font-medium">אזל מהמלאי</span>
                </>
              )}
            </div>

            {/* Quantity and Add to Cart - Mobile-First (Hidden on mobile, shown as sticky bar) */}
            <div className="hidden lg:block space-y-4">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">כמות:</label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    className="p-2 hover:bg-gray-100 touch-manipulation"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={product.stock_quantity}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 text-center border-0 focus:ring-0"
                  />
                  <button
                    onClick={() => setQuantity(prev => Math.min(product.stock_quantity, prev + 1))}
                    className="p-2 hover:bg-gray-100 touch-manipulation"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity === 0 || cartLoading}
                  className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 touch-manipulation"
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  <span>{cartLoading ? 'מוסיף...' : 'הוסף לעגלה'}</span>
                </button>
                
                <button
                  onClick={handleWishlistToggle}
                  className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center touch-manipulation"
                >
                  {isWishlisted ? (
                    <HeartIconSolid className="h-5 w-5 text-red-500" />
                  ) : (
                    <HeartIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                <button
                  onClick={handleShare}
                  className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center touch-manipulation"
                >
                  <ShareIcon className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3 pt-6 border-t">
              <div className="flex items-center space-x-3">
                <TruckIcon className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">משלוח חינם מעל ₪300</span>
              </div>
              <div className="flex items-center space-x-3">
                <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-gray-700">אחריות מלאה למוצר</span>
              </div>
              <div className="flex items-center space-x-3">
                <TagIcon className="h-5 w-5 text-purple-600" />
                <span className="text-sm text-gray-700">החזרה עד 30 יום</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="border-t pt-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {['description', 'specifications', 'reviews'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'description' && 'תיאור'}
                  {tab === 'specifications' && 'מפרט טכני'}
                  {tab === 'reviews' && `ביקורות (${reviews.length})`}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-8">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {product.description 
                    ? (showFullDescription ? product.description : `${product.description.substring(0, 300)}${product.description.length > 300 ? '...' : ''}`)
                    : 'אין תיאור זמין'}
                </p>
                {product.description?.length > 300 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-primary-600 hover:text-primary-700 font-medium mt-2"
                  >
                    {showFullDescription ? 'הצג פחות' : 'הצג עוד'}
                  </button>
                )}
              </div>
            )}

            {activeTab === 'specifications' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {product.specifications ? (
                  Object.entries(product.specifications).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium text-gray-900">{key}</span>
                      <span className="text-gray-600">{value}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">אין מפרט טכני זמין</p>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {/* Add Review Button */}
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">ביקורות לקוחות</h3>
                  {user && !showReviewForm && (
                    <button
                      onClick={() => setShowReviewForm(true)}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      כתוב ביקורת
                    </button>
                  )}
                  {!user && (
                    <button
                      onClick={() => navigate('/login')}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      התחבר לכתיבת ביקורת
                    </button>
                  )}
                </div>

                {/* Review Form */}
                {showReviewForm && (
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="text-lg font-medium mb-4">כתוב ביקורת</h4>
                    <form onSubmit={handleReviewSubmit} className="space-y-4">
                      {/* Rating */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          דירוג
                        </label>
                        <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                              className="focus:outline-none"
                            >
                              {star <= reviewForm.rating ? (
                                <StarIconSolid className="h-6 w-6 text-yellow-400" />
                              ) : (
                                <StarIcon className="h-6 w-6 text-gray-300" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          כותרת (אופציונלי)
                        </label>
                        <input
                          type="text"
                          value={reviewForm.title}
                          onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                          placeholder="כותרת לביקורת"
                          maxLength={200}
                        />
                      </div>

                      {/* Comment */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          תגובה
                        </label>
                        <textarea
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                          rows={4}
                          placeholder="שתף את החוויה שלך עם המוצר..."
                          maxLength={2000}
                        />
                      </div>

                      {/* Form Actions */}
                      <div className="flex space-x-3">
                        <button
                          type="submit"
                          disabled={submittingReview}
                          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submittingReview ? 'שולח...' : 'שלח ביקורת'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowReviewForm(false)}
                          className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
                        >
                          ביטול
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Reviews List */}
                <div className="space-y-4">
                  {reviews.length > 0 ? (
                    reviews.map((review) => (
                      <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-gray-900">{review.user_name}</span>
                            <div className="flex">{renderStars(review.rating)}</div>
                            {review.is_verified_purchase && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                קנייה מאומתת
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(review.created_at).toLocaleDateString('he-IL')}
                          </span>
                        </div>
                        {review.title && (
                          <h5 className="font-medium text-gray-900 mb-2">{review.title}</h5>
                        )}
                        <p className="text-gray-700">{review.comment}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">אין ביקורות עדיין</p>
                      <p className="text-sm text-gray-400">היה הראשון לכתוב ביקורת על המוצר הזה</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products - Old (keep for fallback) */}
        {relatedProducts.length > 0 && (
          <div className="border-t pt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">מוצרים דומים</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.id}`}
                  className="group"
                >
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3">
                    <img
                      src={relatedProduct.images?.[0] || '/placeholder-product.jpg'}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
                    {relatedProduct.name}
                  </h3>
                  <p className="text-primary-600 font-bold">
                    {formatPrice(relatedProduct.price_b2c)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* AI-Powered Related Products - "Customers who bought this also bought" */}
        <RelatedProducts productId={id} limit={8} />
      </div>

      {/* Mobile Sticky Bottom Bar - Only on mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-50 safe-area-bottom">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            {/* Quantity Controls */}
            <div className="flex items-center border border-gray-300 rounded-lg bg-white">
              <button
                onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                className="p-3 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                aria-label="Decrease quantity"
              >
                <MinusIcon className="h-5 w-5" />
              </button>
              <span className="w-12 text-center font-medium">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(prev => Math.min(product.stock_quantity, prev + 1))}
                className="p-3 hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                aria-label="Increase quantity"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={product.stock_quantity === 0 || cartLoading}
              className="flex-1 bg-primary-600 text-white px-6 py-3.5 rounded-lg font-medium hover:bg-primary-700 active:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation shadow-sm"
            >
              <ShoppingCartIcon className="h-5 w-5" />
              <span>{cartLoading ? 'מוסיף...' : 'הוסף לעגלה'}</span>
              <span className="font-bold">• {formatPrice(currentPrice * quantity)}</span>
            </button>

            {/* Wishlist Button */}
            <button
              onClick={handleWishlistToggle}
              className="p-3.5 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center touch-manipulation bg-white"
              aria-label="Add to wishlist"
            >
              {isWishlisted ? (
                <HeartIconSolid className="h-6 w-6 text-red-500" />
              ) : (
                <HeartIcon className="h-6 w-6 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
